/* ============================================================================
   Search drawer UI. Logic lives in nwa-search.js (window.NWASearchCore); this
   file is only presentation and event wiring.
   ========================================================================== */
(function () {
  'use strict';

  var C = window.NWASearchCore;
  if (!C) return;

  var root, panel, input, body, foot, lastFocus;
  /* Two presentations of the SAME results. 'dropdown' hangs under a real field
     on the page and that field stays the thing being typed into; 'drawer' is
     the full-screen panel with its own input. Every entry point on the site now
     has a field, so 'drawer' is the fallback that only runs if a trigger
     without one is ever reintroduced (the old header search icon). */
  var mode = 'drawer';
  var activeInput = null;
  var anchorField = null;
  /* The panel's height is not known when it opens - results arrive from a fetch
     and the panel grows afterwards, and again on every keystroke that changes
     the result set. The exit scrim is parked against its bottom edge, so that
     edge has to be re-read whenever it moves rather than once on open. */
  var panelRO = null;
  var timer = null;
  /* Every render is stamped with the sequence number of the keystroke that asked
     for it. Product and chip fetches resolve out of order, so a response whose
     stamp is no longer current is dropped rather than painted over newer text. */
  var seq = 0;

  function esc(s) {
    return String(s == null ? '' : s).replace(/[&<>"']/g, function (c) {
      return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c];
    });
  }

  /* Predictive search returns price as a decimal STRING already in the ACTIVE
     market currency ("14600.00"), not in cents. Verified against /products/x.js
     and the og:price meta on the same product.

     The currency is whatever market the shopper is in, so the symbol cannot be
     hardcoded: this store serves INR alongside USD, and a hardcoded "$" renders
     a 14,600 rupee tee as $14,600. Read the active currency off Shopify.currency
     and let Intl place the symbol and separators for it. */
  function money(value, currency) {
    var n = Number(value);
    if (!isFinite(n)) return '';
    /* Prefer the currencyCode that came with the amount — it cannot disagree
       with the number. Predictive search sends no code, so fall back to the
       active market currency there. */
    var cur = currency || (window.Shopify && window.Shopify.currency &&
               window.Shopify.currency.active) || 'USD';
    try {
      return new Intl.NumberFormat(undefined, {
        style: 'currency', currency: cur, minimumFractionDigits: 2
      }).format(n);
    } catch (e) {
      return n.toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    }
  }

  /* ---------- open / close ---------------------------------------------- */

  function termOf() {
    return ((activeInput && activeInput.value) || '').trim();
  }

  function fieldOf(trigger) {
    if (!trigger) return null;
    /* The trigger may BE the field (results page) or merely contain one (the
       header form and the sticky bar), so handle both. */
    if (trigger.matches && trigger.matches('input[name="q"]')) return trigger;
    return trigger.querySelector ? trigger.querySelector('input[name="q"]') : null;
  }

  /* Anchor the dropdown under the field it belongs to. Recomputed on scroll and
     resize because the sticky bar moves, and on phones because the on-screen
     keyboard shrinks the visual viewport - sizing against innerHeight there
     runs the panel down behind the keyboard where it cannot be read. */
  /* The bordered box is the FORM (.sb__form / .headsh-search), not the <input>
     inside it, so that is what the panel has to line up with. Measuring the
     input instead is what made the panel wider than the box it hangs from. */
  function boxOf(field) {
    return (field && field.closest && field.closest('[data-nwa-search-trigger]')) || field;
  }

  function position() {
    if (mode !== 'dropdown' || !anchorField || !panel) return;
    var box = boxOf(anchorField);
    var r = box.getBoundingClientRect();
    var cs = window.getComputedStyle(box);
    var bw = parseFloat(cs.borderLeftWidth) || 1;
    var vv = window.visualViewport;
    var vh = vv ? vv.height : window.innerHeight;
    var narrow = window.matchMedia('(max-width: 1100px)').matches;

    var scrim = root.querySelector('.nwa-search__scrim');
    var top, room;
    panel.style.borderStyle = 'solid';

    if (narrow) {
      /* Phone and tablet: hang off the search box and share its outline. Only
         the WIDTH is read from the live box (1.5px on the sticky bar, 1px in
         the header); the COLOUR comes from --nwa-search-accent in CSS, because
         sampling it here races the field's :focus-within rule and copies the
         unfocused grey - a grey panel on a red box, the seam this removes. */
      panel.style.borderWidth = bw + 'px';
      panel.style.borderTopWidth = '0px';
      /* Sit ON the field's bottom border, not below it: the panel's own white
         background hides that edge so the two share one unbroken outline. */
      top = Math.round(r.bottom - bw);
      panel.style.left = Math.round(r.left) + 'px';
      panel.style.width = Math.round(r.width) + 'px';
      /* Stop short of the bottom so there is somewhere to tap to get out. The
         scrim is moved under the panel below to catch that tap, so it closes
         the dropdown instead of hitting whatever product link is behind it. */
      var exitGap = Math.max(76, Math.round(vh * 0.14));
      room = Math.round(vh - top - exitGap);
    } else {
      /* Desktop: full width under the header, matching the mega menu rather
         than the search field. A panel the width of that slim field is too
         narrow to lay results out in. No border here - a full-bleed bar cannot
         read as an extension of a 260px box, so it uses the menu's shadow
         instead and the content is capped and centred to the same 1120px. */
      var hdr = document.querySelector('.headsh-header');
      top = hdr ? Math.round(hdr.getBoundingClientRect().bottom)
                : Math.round(r.bottom - bw);
      panel.style.borderWidth = '0px';
      panel.style.left = '0px';
      panel.style.width = '100%';
      room = Math.min(Math.round(vh - top - 12), 560);
    }

    panel.style.top = top + 'px';
    panel.style.maxHeight = Math.max(180, room) + 'px';

    /* Park the scrim directly under the panel so the gap below it is a real
       exit target. Read the panel back rather than computing it: max-height is
       a cap, and a short result set ends well above it. */
    if (scrim) {
      scrim.style.top = narrow
        ? Math.round(panel.getBoundingClientRect().bottom) + 'px'
        : '';
    }
  }

  /* Opened by TYPING in one of the page's own search fields. That field keeps
     focus the whole time, so nothing is stolen: the keyboard never drops and
     the caret never jumps. */
  function openDropdown(field) {
    if (!root.hidden && mode === 'dropdown' && anchorField === field) { position(); return; }
    if (!root.hidden) closeNow();
    mode = 'dropdown';
    anchorField = field;
    activeInput = field;
    root.classList.add('nwa-search--dropdown');
    root.hidden = false;
    position();
    requestAnimationFrame(function () {
      root.classList.add('is-open');
      /* Position again on the next frame, once layout from the opening panel
         has settled and the field's final box is known. */
      position();
    });
    if (window.ResizeObserver) {
      if (!panelRO) panelRO = new window.ResizeObserver(function () { position(); });
      /* Safe from feedback: position() derives max-height from the viewport and
         the anchor, never from the panel's own height, so re-running it on a
         resize cannot change the size that triggered it. */
      panelRO.observe(panel);
    }
    field.addEventListener('keydown', onKeydown);
    field.addEventListener('keydown', onSubmit);
    window.addEventListener('scroll', position, true);
    window.addEventListener('resize', position);
    if (window.visualViewport) window.visualViewport.addEventListener('resize', position);
  }

  /* Fallback for a trigger with no field of its own, so the panel brings one. */
  function openDrawer(seed) {
    if (!root.hidden) return;
    mode = 'drawer';
    anchorField = null;
    activeInput = input;
    root.classList.remove('nwa-search--dropdown');
    lastFocus = document.activeElement;
    root.hidden = false;
    document.documentElement.style.overflow = 'hidden';
    if (seed) { input.value = seed; }
    requestAnimationFrame(function () {
      root.classList.add('is-open');
      input.focus();
      /* Caret to the end so the customer carries on typing where they left off
         in the header field rather than in front of what they already typed. */
      try { input.setSelectionRange(input.value.length, input.value.length); } catch (e) {}
      if (seed) onInput();
    });
  }

  function detach() {
    if (panelRO) panelRO.disconnect();
    if (anchorField) {
      anchorField.removeEventListener('keydown', onKeydown);
      anchorField.removeEventListener('keydown', onSubmit);
    }
    window.removeEventListener('scroll', position, true);
    window.removeEventListener('resize', position);
    if (window.visualViewport) window.visualViewport.removeEventListener('resize', position);
  }

  /* Torn down with no animation - only used when one presentation replaces the
     other, where an animating leftover would be visible under the new one. */
  function closeNow() {
    detach();
    seq++;
    window.clearTimeout(timer);
    root.classList.remove('is-open', 'nwa-search--dropdown');
    root.hidden = true;
    document.documentElement.style.overflow = '';
    anchorField = null;
    activeInput = input;
    mode = 'drawer';
  }

  function close() {
    if (root.hidden) return;
    var wasDrawer = mode === 'drawer';
    detach();
    root.classList.remove('is-open');
    if (wasDrawer) document.documentElement.style.overflow = '';
    window.setTimeout(function () {
      root.hidden = true;
      root.classList.remove('nwa-search--dropdown');
      /* The drawer owns its input, so it clears it. A dropdown does not own the
         page's field and must leave what the customer typed exactly as it is. */
      if (wasDrawer) input.value = '';
      renderIdle();
    }, 180);
    /* Returning focus is right for a modal drawer. For a dropdown the customer
       is still in the field, and yanking focus back would reopen the keyboard
       they just dismissed. */
    if (wasDrawer && lastFocus && lastFocus.focus) lastFocus.focus();
    anchorField = null;
    activeInput = input;
    mode = 'drawer';
  }

  /* ---------- rendering -------------------------------------------------- */

  function renderIdle() {
    body.innerHTML =
      '<div class="nwa-search__empty">' +
      '<p class="nwa-search__hint">Start typing a brand, category or product.</p>' +
      '</div>';
    /* :empty hides the footer bar, so clearing it removes the rule above it. */
    if (foot) foot.innerHTML = '';
  }

  function brandRowHtml(brand, extras) {
    var html =
      '<a class="nwa-search__brand" href="' + esc(C.brandUrl(brand)) + '" data-nwa-item>' +
      '<span class="nwa-search__brand-name">' + esc(brand.title) + '</span>' +
      '<span class="nwa-search__brand-meta">' + esc(brand.count || 0) + ' products</span>' +
      '<span class="nwa-search__brand-go" aria-hidden="true">Shop all &rsaquo;</span>' +
      '</a>';
    if (extras && extras.length) {
      html += '<div class="nwa-search__also">' +
        extras.map(function (b) {
          return '<a class="nwa-search__also-link" href="' + esc(C.brandUrl(b)) +
            '" data-nwa-item>' + esc(b.title) + '</a>';
        }).join('') + '</div>';
    }
    return '<section class="nwa-search__block">' +
      '<h2 class="nwa-search__label">Brand</h2>' + html + '</section>';
  }

  function chipsHtml(brand, types) {
    if (!types || !types.length) return '';
    return '<section class="nwa-search__block" data-nwa-chips>' +
      '<h2 class="nwa-search__label">Shop ' + esc(brand.title) + ' by category</h2>' +
      '<div class="nwa-search__chips">' +
      types.map(function (t) {
        return '<a class="nwa-search__chip" href="' +
          esc(C.brandTypeUrl(brand, t.label)) + '" data-nwa-item>' +
          esc(t.label) + '<span class="nwa-search__chip-n">' + esc(t.count) + '</span></a>';
      }).join('') +
      '</div></section>';
  }

  /* Brands that sell this category. The mirror of the type chips, and the answer
     to a category-first query: "hoodie" should open up the brands that sell
     hoodies, not pick one of them. */
  function brandChipsHtml(cat, vendors) {
    if (!vendors || !vendors.length) return '';
    return '<section class="nwa-search__block">' +
      '<h2 class="nwa-search__label">Shop ' + esc(cat.title) + ' by brand</h2>' +
      '<div class="nwa-search__chips">' +
      vendors.slice(0, 12).map(function (v) {
        return '<a class="nwa-search__chip" href="' +
          esc(C.allUrl({ type: cat.type, vendor: v.label })) + '" data-nwa-item>' +
          esc(v.label) + '<span class="nwa-search__chip-n">' + esc(v.count) + '</span></a>';
      }).join('') +
      '</div></section>';
  }

  /* Append the colour facet to an already-built collection URL. */
  function withColour(url, colour) {
    if (!colour) return url;
    return url + (url.indexOf('?') === -1 ? '?' : '&') + C.colourParam(colour);
  }

  /* Where a category row should land.

     Category alone -> the category collection, which is the natural landing page
     and has nothing to clear. Category PLUS a colour -> /collections/all carrying
     both as filters, so both show as removable chips. Landing on
     /collections/hoodies?<colour> would pin "hoodies" as the collection itself
     and leave the shopper able to clear only the colour. */
  function categoryDest(cat, colour) {
    if (!colour) return C.catUrl(cat);
    return C.allUrl({ type: cat.type, colour: colour });
  }

  function categoryHtml(cat, colour) {
    return '<section class="nwa-search__block">' +
      '<h2 class="nwa-search__label">Category</h2>' +
      '<a class="nwa-search__brand" href="' +
      esc(categoryDest(cat, colour)) + '" data-nwa-item>' +
      '<span class="nwa-search__brand-name">' +
      esc(colour ? colour.title + ' ' + cat.title : cat.title) + '</span>' +
      '<span class="nwa-search__brand-go" aria-hidden="true">Shop all &rsaquo;</span>' +
      '</a></section>';
  }

  function comboHtml(brand, cat, colour) {
    return '<section class="nwa-search__block">' +
      '<a class="nwa-search__combo" href="' +
      esc(withColour(C.brandTypeUrl(brand, cat.type), colour)) +
      '" data-nwa-item>' +
      '<span class="nwa-search__combo-text">' + esc(brand.title) + ' &middot; ' +
      esc(colour ? colour.title + ' ' + cat.title : cat.title) + '</span>' +
      '<span class="nwa-search__brand-go" aria-hidden="true">View &rsaquo;</span>' +
      '</a></section>';
  }

  function productsHtml(products, q, label) {
    if (!products.length) {
      return '<section class="nwa-search__block"><p class="nwa-search__none">' +
        'No products match &ldquo;' + esc(q) + '&rdquo;.</p></section>';
    }
    return '<section class="nwa-search__block">' +
      '<h2 class="nwa-search__label">' + esc(label || 'Products') + '</h2>' +
      '<div class="nwa-search__products">' +
      products.map(function (p) {
        var img = p.featured_image && p.featured_image.url;
        return '<a class="nwa-search__product" href="' + esc(p.url) + '" data-nwa-item>' +
          (img
            ? '<img class="nwa-search__product-img" src="' + esc(img) +
              '" alt="" loading="lazy" width="56" height="56">'
            : '<span class="nwa-search__product-img nwa-search__product-img--empty"></span>') +
          '<span class="nwa-search__product-text">' +
          '<span class="nwa-search__product-title">' + esc(p.title) + '</span>' +
          '<span class="nwa-search__product-meta">' +
          esc([p.vendor, p.type || p.product_type].filter(Boolean).join(' \u00b7 ')) + '</span>' +
          '</span>' +
          '<span class="nwa-search__product-price">' + esc(money(p.price, p.currency)) + '</span>' +
          '</a>';
      }).join('') +
      '</div></section>';
  }

  function footerHtml(q, opts) {
    return '<a class="nwa-search__all" data-nwa-all href="' +
      esc(C.searchUrl(q, opts)) + '" data-nwa-item>' +
      'See all results for &ldquo;' + esc(q) + '&rdquo;</a>';
  }

  /* ---------- the main render pass --------------------------------------- */

  function slot(name) { return body.querySelector('[data-nwa-' + name + '-slot]'); }
  function setChips(html) { var el = slot('chips'); if (el) el.innerHTML = html; }
  function setProducts(html) { var el = slot('products'); if (el) el.innerHTML = html; }
  function setAllHref(href) {
    var el = foot && foot.querySelector('[data-nwa-all]');
    if (el) el.setAttribute('href', href);
  }

  /* Storefront filter inputs for the facets we actually resolved. */
  function productFilters(cat, colour) {
    var f = [];
    if (cat) f.push({ productType: cat.type });
    if (colour) {
      f.push({ taxonomyMetafield: {
        namespace: 'shopify', key: 'color-pattern',
        value: 'gid://shopify/TaxonomyValue/' + colour.gid
      } });
    }
    return f;
  }

  function colourHtml(colour) {
    return '<section class="nwa-search__block">' +
      '<h2 class="nwa-search__label">Colour</h2>' +
      '<a class="nwa-search__brand" href="' +
      esc(C.allUrl({ colour: colour })) + '" data-nwa-item>' +
      '<span class="nwa-search__brand-name">' + esc(colour.title) + '</span>' +
      '<span class="nwa-search__brand-go" aria-hidden="true">Shop all &rsaquo;</span>' +
      '</a></section>';
  }

  function relevanceFallback(q, mine) {
    C.fetchProducts(q).then(function (ps) {
      if (mine === seq) setProducts(productsHtml(ps, q, 'Products'));
    });
  }

  function run(q) {
    var mine = ++seq;
    var parsed = C.parseQuery(q);
    var brand = parsed.brand;
    var cat = parsed.category;
    /* Words the entities did not account for — "black" in "black hoodies".
       Best sellers of the whole category would ignore them, so anything left
       over sends the product list back to text search. Entity rows and chips
       still show: the customer can still jump to Hoodies. */
    var colour = parsed.colour;
    var leftover = parsed.residual;

    /* Entity rows paint immediately — no awaiting the network. */
    var head = '';
    if (brand && cat) head += comboHtml(brand, cat, colour);
    if (brand) {
      var others = C.matchEntities(q, C.BRANDS).filter(function (b) {
        return b.handle !== brand.handle;
      }).slice(0, 3);
      head += brandRowHtml(brand, others);
    }
    if (!brand && cat) head += categoryHtml(cat, colour);

    body.innerHTML = head +
      '<div data-nwa-chips-slot></div>' +
      '<div data-nwa-products-slot><p class="nwa-search__loading">Searching&hellip;</p></div>';

    /* A brand filter belongs on the results link ONLY when a brand was typed.
       For a bare category the customer asked for hoodies, not one label's. */
    if (foot) {
      foot.innerHTML = footerHtml(q, (!brand && (cat || colour))
        ? { type: cat ? cat.type : null, colour: colour }
        : null);
    }

    /* ---- a brand was named: show what that brand sells ---- */
    if (brand) {
      C.fetchFacets(brand.handle).then(function (facets) {
        if (mine !== seq) return;
        setChips(chipsHtml(brand, facets.types));
        if (facets.vendor) {
          setAllHref(C.searchUrl(q, {
            vendor: facets.vendor,
            type: cat ? cat.type : null
          }));
        }
      });
      if (leftover) { relevanceFallback(q, mine); return; }
      C.fetchBestSellers(brand.handle, productFilters(cat, colour))
        .then(function (list) {
          if (mine !== seq) return;
          if (list.length) {
            setProducts(productsHtml(list, q,
              'Best selling ' + brand.title + (cat ? ' ' + cat.title : '')));
          } else {
            relevanceFallback(q, mine);
          }
        });
      return;
    }

    /* ---- only a category: open up the BRANDS that sell it ---- */
    if (cat) {
      C.fetchFacets(cat.handle).then(function (facets) {
        if (mine !== seq) return;
        setChips(brandChipsHtml(cat, facets.vendors));
      });
      if (leftover) { relevanceFallback(q, mine); return; }
      C.fetchBestSellers(cat.handle, productFilters(null, colour)).then(function (list) {
        if (mine !== seq) return;
        if (list.length) setProducts(productsHtml(list, q, 'Best selling ' + cat.title));
        else relevanceFallback(q, mine);
      });
      return;
    }

    /* ---- only a colour ---- */
    if (colour) {
      setChips('');
      body.insertAdjacentHTML('afterbegin', colourHtml(colour));
      if (foot) foot.innerHTML = footerHtml(q, null);
      relevanceFallback(q, mine);
      return;
    }

    /* ---- nothing resolved: relevance, then try to name the brand ---- */
    C.fetchProducts(q).then(function (products) {
      if (mine !== seq) return;
      setProducts(productsHtml(products, q, 'Products'));
      inferBrand(products, q, mine);
    });
  }

  /* Back-inference runs ONLY when neither a brand nor a category resolved.
     It exists for misspelled brands — "spider" returns Sp5der products, so the
     brand can be named from the vendor of the results. Shopify's own semantic
     matching is what relates the two, so there is nothing to configure.
     It must never run on a category query: "hoodie"
     would pick whichever label happens to top the results and wrongly present
     the whole category as that one brand. */
  function inferBrand(products, q, mine) {
    if (!products.length) return;
    var counts = {};
    products.forEach(function (p) {
      if (p.vendor) counts[p.vendor] = (counts[p.vendor] || 0) + 1;
    });
    var top = Object.keys(counts).sort(function (a, b) {
      return counts[b] - counts[a];
    })[0];
    /* Vote share alone cannot tell a misspelled brand from a plain description.
       Measured against the live catalogue, "puffer jacket" returns 6/6 Supreme
       and "long sleeve" 6/6 Vale Forever, so even demanding unanimity still
       announces a brand the customer never asked for - which is how "hooded
       down" became Righteous. */
    if (products.length < 3) return;
    if (!top || counts[top] < Math.ceil(products.length * 0.8)) return;
    var hit = C.matchEntities(top, C.BRANDS, { whole: true })[0];
    if (!hit || mine !== seq) return;
    /* The decisive test: would the QUERY ITSELF match this brand? Back-inference
       exists for brand names typed oddly - "spider" for Sp5der - and in every
       one of those the query does resemble the label. A description never does.
       Reusing the dictionary's own matcher keeps one definition of "looks like
       this brand" instead of inventing a second threshold here. */
    if (!C.matchEntities(q, [hit], { whole: true }).length) return;
    body.insertAdjacentHTML('afterbegin', brandRowHtml(hit, []));
    C.fetchFacets(hit.handle).then(function (facets) {
      if (mine !== seq) return;
      setChips(chipsHtml(hit, facets.types));
      /* Deliberately NO setAllHref here. The brand row above is a SUGGESTION
         the customer can take or ignore; "See all results" - and Enter, which
         follows the same href - has to stay on what they actually typed.
         Narrowing it to a guessed vendor is how Enter on "hooded down" landed
         in the Righteous collection instead of the down jackets. A resolved
         brand or category still narrows the href, up in the main render pass;
         only this inferred-from-results path is barred from touching it. */
    });
  }

  /* ---------- events ------------------------------------------------------ */

  function onInput() {
    var q = termOf();
    window.clearTimeout(timer);
    if (q.length < C.MIN_CHARS) { seq++; renderIdle(); return; }
    timer = window.setTimeout(function () { run(q); }, C.DEBOUNCE);
  }

  function onKeydown(e) {
    if (e.key === 'Escape') { close(); return; }
    var items = Array.prototype.slice.call(
      panel.querySelectorAll('[data-nwa-item]'));
    if (!items.length) return;
    var i = items.indexOf(document.activeElement);
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      (items[i + 1] || items[0]).focus();
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      if (i <= 0) (activeInput || input).focus(); else items[i - 1].focus();
    }
  }

  function onSubmit(e) {
    if (e.key !== 'Enter' || document.activeElement !== activeInput) return;
    var q = termOf();
    if (!q) return;
    e.preventDefault();
    /* Enter goes wherever "See all results" points, rather than rebuilding the
       URL. That link already carries whatever facets resolved, so the two cannot
       disagree — before this, Enter gave the raw OR-matched page (1,048 results
       for "black hoodies") while the button gave the narrowed one.

       Still the results page, never a guessed destination: the entity rows are
       right there if the customer wanted one of those instead. */
    var all = foot && foot.querySelector('[data-nwa-all]');
    window.location.href = (all && all.getAttribute('href')) || C.searchUrl(q);
  }

  function init() {
    root = document.getElementById('nwa-search');
    if (!root) return;
    panel = root.querySelector('.nwa-search__panel');
    input = document.getElementById('nwa-search-input');
    body = document.getElementById('nwa-search-body');
    foot = document.getElementById('nwa-search-foot');

    document.addEventListener('click', function (e) {
      /* Text nodes and the document itself have no closest(); guard before use. */
      if (!e.target || typeof e.target.closest !== 'function') return;

      if (e.target.closest('[data-nwa-close]')) {
        e.preventDefault();
        /* Blur first: on a phone "exit" means the keyboard goes away too, and
           close() deliberately leaves dropdown focus alone for every other path. */
        if (mode === 'dropdown' && anchorField && anchorField.blur) anchorField.blur();
        close();
        return;
      }

      var trigger = e.target.closest('[data-nwa-search-trigger], .nwa-search-open');
      if (trigger) {
        /* Never swallow a real submit - the plain GET is the no-JS fallback. */
        if (e.target.closest('[type="submit"]')) return;
        var field = fieldOf(trigger);
        /* A trigger that HAS a field is a box the customer can type in, so the
           tap belongs to that box: put the caret in it and show nothing yet.
           Results follow as a dropdown once there is something to search for.
           Opening a panel on tap - before a single character exists, only to
           say "start typing" - is the step this design removes. */
        if (field) {
          if (e.target !== field) { e.preventDefault(); field.focus(); }
          return;
        }
        /* No field: fall back to the panel bringing its own. */
        e.preventDefault();
        openDrawer('');
        return;
      }

      /* A click anywhere else dismisses an open dropdown. */
      if (mode === 'dropdown' && !root.hidden &&
          !e.target.closest('.nwa-search__panel')) close();
    });

    /* Every search field on the page drives the same pipeline as the panel's own
       input; a field opens the dropdown once it passes MIN_CHARS, and closes it
       again if the customer deletes back below that. */
    document.addEventListener('input', function (e) {
      var t = e.target;
      if (!t || t === input || !t.matches || !t.matches('input[name="q"]')) return;
      if (!t.closest('[data-nwa-search-trigger]')) return;
      if (t.value.trim().length < C.MIN_CHARS) {
        if (mode === 'dropdown' && !root.hidden) close();
        return;
      }
      openDropdown(t);
      onInput();
    });

    input.addEventListener('input', onInput);
    input.addEventListener('keydown', onSubmit);
    root.addEventListener('keydown', onKeydown);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
